import { useState, FormEvent } from "react";

export default function Footer() {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleNewsletter = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setNewsletterStatus("loading");

    const data = new URLSearchParams();
    data.append("email", newsletterEmail);

    try {
      const res = await fetch("https://readdy.ai/api/form/d7dq39em3fsfopf23vr0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setNewsletterStatus("success");
        setNewsletterEmail("");
      } else {
        setNewsletterStatus("error");
      }
    } catch {
      setNewsletterStatus("error");
    }
  };

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-[#0d1a1a] text-white">
      {/* Top Section */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="https://static.readdy.ai/image/9d8cfb7979fc745e2d4072c466ad6183/3ac0f563bfa6cda8ff586688bad6c56b.jpeg"
                alt="Albert Vidente"
                className="w-12 h-12 rounded-full object-cover border-2 border-[#c9a84c]"
              />
              <div>
                <p className="text-[#c9a84c] font-serif text-lg font-bold leading-tight">Albert Vidente</p>
                <p className="text-white/50 text-xs tracking-widest uppercase">Tarotista Profesional</p>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-4 max-w-xs">
              Guía espiritual online con más de 15 años de experiencia. Lecturas de tarot precisas y videncia personalizada.
            </p>
            {/* Languages */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-white/40 text-xs">Atención en:</span>
              {[
                { flag: "🇪🇸", lang: "Español" },
                { flag: "🇬🇧", lang: "English" },
                { flag: "🇵🇱", lang: "Polski" },
              ].map((l) => (
                <span key={l.lang} className="flex items-center gap-1 px-2 py-0.5 bg-white/5 border border-white/10 rounded-full text-white/60 text-xs">
                  <span>{l.flag}</span> {l.lang}
                </span>
              ))}
            </div>
            {/* WhatsApp */}
            <a
              href="https://wa.me/34673410005"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600/20 border border-green-500/30 text-green-400 text-sm rounded-full hover:bg-green-600/30 transition-colors cursor-pointer mb-5 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base"></i>
              +34 673 41 00 05
            </a>

            {/* Newsletter */}
            <p className="text-white/80 text-sm font-semibold mb-3">Recibe sabiduría espiritual</p>
            <form
              onSubmit={handleNewsletter}
              data-readdy-form
              id="newsletter-form"
              className="flex gap-2"
            >
              <input
                type="email"
                name="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                className="flex-1 bg-white/10 border border-white/20 text-white text-sm rounded-full px-4 py-2.5 focus:outline-none focus:border-[#c9a84c]/60 placeholder-white/30 min-w-0"
              />
              <button
                type="submit"
                disabled={newsletterStatus === "loading"}
                className="w-10 h-10 flex items-center justify-center bg-[#c9a84c] rounded-full hover:bg-[#e0c06a] transition-colors cursor-pointer flex-shrink-0 disabled:opacity-50"
              >
                <i className="ri-send-plane-line text-black text-sm"></i>
              </button>
            </form>
            {newsletterStatus === "success" && (
              <p className="text-green-400 text-xs mt-2">¡Suscrito con éxito!</p>
            )}
            {newsletterStatus === "error" && (
              <p className="text-red-400 text-xs mt-2">Error al suscribirse. Intenta de nuevo.</p>
            )}
          </div>

          {/* Links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Servicios</h4>
            <ul className="space-y-2.5">
              {["Lectura de Tarot", "Videncia Personal", "Tarot del Amor", "Carta Astral", "Limpieza Energética"].map((item) => (
                <li key={item}>
                  <a
                    href="#servicios"
                    onClick={(e) => { e.preventDefault(); scrollTo("#servicios"); }}
                    className="text-white/55 text-sm hover:text-[#c9a84c] transition-colors cursor-pointer"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Recursos</h4>
            <ul className="space-y-2.5">
              {["Blog Espiritual", "Guía del Tarot", "Astrología", "Meditación", "FAQ"].map((item) => (
                <li key={item}>
                  <a
                    href="#blog"
                    onClick={(e) => { e.preventDefault(); scrollTo("#blog"); }}
                    className="text-white/55 text-sm hover:text-[#c9a84c] transition-colors cursor-pointer"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4 tracking-wide">Legal</h4>
            <ul className="space-y-2.5">
              {["Política de Privacidad", "Términos de Uso", "Aviso Legal", "Cookies"].map((item) => (
                <li key={item}>
                  <a href="#" className="text-white/55 text-sm hover:text-[#c9a84c] transition-colors cursor-pointer">
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs">
            © 2026 Albert Vidente Tarotista. Todos los derechos reservados.
          </p>

          {/* Social Icons */}
          <div className="flex items-center gap-3">
            {[
              { icon: "ri-instagram-line", label: "Instagram" },
              { icon: "ri-facebook-line", label: "Facebook" },
              { icon: "ri-youtube-line", label: "YouTube" },
              { icon: "ri-whatsapp-line", label: "WhatsApp" },
              { icon: "ri-tiktok-line", label: "TikTok" },
            ].map((social) => (
              <a
                key={social.label}
                href="#"
                aria-label={social.label}
                className="w-8 h-8 flex items-center justify-center border border-white/20 rounded-lg text-white/50 hover:border-[#c9a84c] hover:text-[#c9a84c] transition-all duration-300 cursor-pointer"
              >
                <i className={`${social.icon} text-sm`}></i>
              </a>
            ))}
          </div>

          <p className="text-white/30 text-xs">
            Diseñado con <span className="text-[#c9a84c]">♥</span> para guiar tu camino
          </p>
        </div>
      </div>
    </footer>
  );
}
